import React, { FunctionComponent, lazy, useEffect, useRef, useState } from 'react';

import { has, get, isFunction, map } from 'lodash';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Quill } from 'react-quill';

import { Gallery, Props as GalleryProps } from 'common/gallery/Gallery';
import { LinkMetadata } from 'common/link-metadata/LinkMetadata';
import { EmojiBlot } from 'common/response-editor/blots/EmojiBlot';
import { MentionBlot } from 'common/response-editor/blots/MentionBlot';
import { VariableBlot } from 'common/response-editor/blots/VariableBlot';
import { convertMetadataToQuill, MetaData } from 'common/response-editor/MetadataParser';
import { Props as ResponseEditorProps, ResponseEditor } from 'common/response-editor/ResponseEditor';

import 'react-quill/dist/quill.snow.css';
import 'common/response-editor/renderer/ResponseEditorRenderer.scss';

const ReactQuill = lazy(() => import('react-quill'));

Quill.register({ 'formats/variable': VariableBlot }, true);
Quill.register({ 'formats/mention': MentionBlot }, true);
Quill.register({ 'formats/emoji': EmojiBlot }, true);

export interface Props {
    content: MetaData[];
    /** default: false */
    enableEditMode?: boolean;
    responseEditorOptions?: ResponseEditorProps;
    mediaOptions?: GalleryProps;
    /** default: false */
    displayLinkMetadata?: boolean;
}

export const ResponseEditorRenderer: FunctionComponent<Props> = ({
    content = [],
    enableEditMode = false,
    responseEditorOptions,
    mediaOptions,
    displayLinkMetadata = false,
}): JSX.Element => {
    const { t } = useTranslation('common/response-editor');
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [contentData, setContentData] = useState<MetaData[]>(content);
    const [galleryData, setGalleryData] = useState<any>(get(mediaOptions, 'galleryData', []));

    const editorRef = useRef();

    useEffect(() => {
        if (content) {
            setContentData(content);
        }
    }, [content]);

    const editIconClick = (): void => {
        setIsEditMode(true);
    };

    const getText = (): string => {
        return map(contentData, (contentItem) => contentItem.text).join(' ');
    };

    const handleSave = (): void => {
        const node: any = editorRef!.current;

        if (node) {
            node.respond();
        }
    };

    const handleCancel = (): void => {
        setIsEditMode(false);
    };

    return (
        <div className="r4e-response-editor-renderer">
            {isEditMode ? (
                <div className="edit-container">
                    <ResponseEditor
                        {...responseEditorOptions}
                        ref={editorRef}
                        agencyId={responseEditorOptions?.agencyId!}
                        tenantId={responseEditorOptions?.tenantId!}
                        textOptions={{
                            ...responseEditorOptions?.textOptions,
                            defaultText: contentData,
                        }}
                        toolBarOptions={{
                            enable: true,
                            ...responseEditorOptions?.toolBarOptions,
                            specialOptions: {
                                ...responseEditorOptions?.toolBarOptions?.specialOptions,
                                mediaOptions: {
                                    ...responseEditorOptions?.toolBarOptions?.specialOptions?.mediaOptions,
                                    media: galleryData as any,
                                },
                            },
                            respondButtonOptions: {
                                enable: false,
                            },
                        }}
                        handlers={{
                            ...responseEditorOptions?.handlers,
                            onResponseSubmit: (response: any): void => {
                                if (
                                    has(responseEditorOptions, 'handlers.onResponseSubmit') &&
                                    isFunction(responseEditorOptions?.handlers?.onResponseSubmit)
                                ) {
                                    responseEditorOptions!.handlers!.onResponseSubmit(response);
                                }
                                setContentData(response.metadata);
                                setGalleryData(response.media);
                                setTimeout(() => {
                                    setIsEditMode(false);
                                }, 0);
                            },
                        }}
                    />
                    <div className="edit-action-container">
                        <Button variant="secondary" onClick={handleCancel}>
                            {t('CANCEL')}
                        </Button>
                        <Button variant="primary" onClick={handleSave}>
                            {t('SAVE')}
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="read-only-container">
                        <ReactQuill
                            className="read-only-content"
                            value={convertMetadataToQuill(contentData)}
                            modules={{
                                toolbar: false,
                            }}
                            theme="snow"
                            readOnly
                        />
                        {enableEditMode ? (
                            <Button variant="icon" className="icon-pencil" onClick={editIconClick} />
                        ) : null}
                    </div>
                    {displayLinkMetadata && (
                        <LinkMetadata tenantId={responseEditorOptions?.tenantId || -1} text={getText()} />
                    )}
                    <Gallery {...mediaOptions} galleryData={galleryData as any} canDelete={false} />
                </>
            )}
        </div>
    );
};
